import React, { useEffect, useRef, useContext, useState, useCallback } from "react";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Box, Flex, Spinner } from "theme-ui";
import H2 from "components/typography/H2";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import { themevals } from "theme/themevals";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";
import { 
  ChatMessage, 
  ChatSession,
  AIProvider,
  sendMessage, 
  createSession, 
  listSessions,
  getSessionHistory,
  deleteSession,
  updateSessionTitle,
  healthCheck,
  getAvailableTools,
  ToolAccessResponse,
  HealthCheckResponse,
} from "api/AIChat";

// Model options by provider
const MODEL_OPTIONS: Record<AIProvider, { value: string; label: string }[]> = {
  openai: [
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'o1', label: 'o1' },
    { value: 'o1-mini', label: 'o1 Mini' },
    { value: 'o3-mini', label: 'o3 Mini' },
  ],
  claude: [
    { value: 'claude-sonnet-4-20250514', label: 'Claude 4 Sonnet' },
    { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
    { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
    { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
  ],
};

const AIChat = () => {
  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Sessions state
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  
  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toolAccess, setToolAccess] = useState<ToolAccessResponse | null>(null);
  const [healthStatus, setHealthStatus] = useState<HealthCheckResponse | null>(null);

  // Provider and model state
  const [provider, setProvider] = useState<AIProvider>('openai');
  const [model, setModel] = useState<string>('gpt-4o');

  // Edit title state
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | AI Assistant";
  }, []);

  // Update model when provider changes
  useEffect(() => {
    setModel(MODEL_OPTIONS[provider][0].value);
  }, [provider]);

  // Load sessions on mount
  const loadSessions = useCallback(async () => {
    try {
      const sessionList = await listSessions();
      setSessions(sessionList);
    } catch (err) {
      console.error("Failed to load sessions:", err);
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      try {
        const health = await healthCheck();
        setHealthStatus(health);
        if (health.status !== 'healthy') {
          setError("AI Assistant is currently degraded. Some features may not work.");
        }

        const access = await getAvailableTools();
        setToolAccess(access);

        if (!access.canRead) {
          setError("You don't have permission to use the AI Assistant. Contact your administrator.");
          setIsInitializing(false);
          return;
        }

        await loadSessions();
        setIsInitializing(false);
      } catch (err: any) {
        console.error("Failed to initialize:", err);
        setError(err?.message || "Failed to initialize chat. Please refresh the page.");
        setIsInitializing(false);
      }
    };
    init();
  }, [loadSessions]);

  // Track activity
  useEffect(() => {
    if (hasTrackedView.current || !user?.id) return;
    hasTrackedView.current = true;
    void trackActivity({
      module: "ai-assistant",
      action: "view_ai_assistant",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: null,
      entityId: null,
      metadata: null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [user?.id, user?.company?.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load session messages
  const loadSession = useCallback(async (sessionId: string) => {
    setIsLoading(true);
    try {
      const { messages: sessionMessages } = await getSessionHistory(sessionId);
      setMessages(sessionMessages);
      setActiveSessionId(sessionId);
    } catch (err: any) {
      setError("Failed to load chat history");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create new chat
  const handleNewChat = useCallback(async () => {
    setActiveSessionId(null);
    setMessages([]);
    setError(null);
  }, []);

  // Delete session
  const handleDeleteSession = useCallback(async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm("Delete this chat?")) return;
    
    try {
      await deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setMessages([]);
      }
    } catch (err) {
      setError("Failed to delete chat");
    }
  }, [activeSessionId]);

  // Save title
  const handleSaveTitle = useCallback(async (sessionId: string) => {
    if (!editTitle.trim()) {
      setEditingSessionId(null);
      return;
    }
    try {
      await updateSessionTitle(sessionId, editTitle.trim());
      setSessions(prev => prev.map(s => 
        s.id === sessionId ? { ...s, title: editTitle.trim() } : s
      ));
    } catch (err) {
      setError("Failed to rename chat");
    }
    setEditingSessionId(null);
  }, [editTitle]);

  // Send message
  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessageContent = inputValue.trim();
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: userMessageContent,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setError(null);

    try {
      const response = await sendMessage(
        activeSessionId || undefined,
        userMessageContent,
        messages,
        provider,
        model
      );

      // Update active session
      if (!activeSessionId && response.sessionId) {
        setActiveSessionId(response.sessionId);
        await loadSessions(); // Refresh list to get new session
      }

      const toolMessages = response.messages.filter(m => m.role === 'tool');
      const toolCalls = toolMessages.map(m => ({
        name: m.toolCallId || 'tool',
        result: m.content,
      }));

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: response.response,
        timestamp: new Date().toISOString(),
        toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err: any) {
      console.error("Failed to send message:", err);
      setError(err?.message || "Failed to send message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, activeSessionId, messages, isLoading, loadSessions, provider, model]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isInitializing) {
    return (
      <>
        <Header />
        <Page title="AI Assistant" subtitle="Your intelligent business assistant">
          <Flex sx={{ justifyContent: "center", alignItems: "center", height: "400px", flexDirection: "column", gap: "16px" }}>
            <Spinner size={48} />
            <P>Initializing AI Assistant...</P>
          </Flex>
        </Page>
      </>
    );
  }

  return (
    <>
      <Header />
      <Page title="AI Assistant" subtitle="Your intelligent business assistant">
        <Flex sx={{ gap: "20px", height: "calc(100vh - 320px)", minHeight: "400px" }}>
          {/* Sidebar */}
          <Box sx={{
            width: "280px",
            flexShrink: 0,
            borderRadius: "8px",
            border: `1px solid ${themevals.colors.mdgrey}`,
            backgroundColor: themevals.colors.white,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}>
            {/* New Chat Button */}
            <Box sx={{ padding: "16px", borderBottom: `1px solid ${themevals.colors.ltgrey}` }}>
              <BasicButton 
                scheme="primary" 
                onClick={handleNewChat}
                styles={{ width: "100%" }}
              >
                + New Chat
              </BasicButton>
            </Box>

            {/* Sessions List */}
            <Box sx={{ flex: 1, overflowY: "auto", padding: "8px" }}>
              {sessions.length === 0 ? (
                <P fontSize="13px" color={themevals.colors.dkgrey} sx={{ textAlign: "center", padding: "20px" }}>
                  No chats yet
                </P>
              ) : (
                sessions.map(session => (
                  <Box
                    key={session.id}
                    onClick={() => loadSession(session.id)}
                    sx={{
                      padding: "12px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: activeSessionId === session.id ? themevals.colors.ltgrey : "transparent",
                      "&:hover": {
                        backgroundColor: themevals.colors.ltgrey,
                      },
                      marginBottom: "4px",
                    }}
                  >
                    {editingSessionId === session.id ? (
                      <Flex sx={{ gap: "8px", alignItems: "center" }}>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            value={editTitle}
                            onChange={(val: string) => setEditTitle(val)}
                            onKeyDown={(e: React.KeyboardEvent) => {
                              if (e.key === "Enter") handleSaveTitle(session.id);
                              if (e.key === "Escape") setEditingSessionId(null);
                            }}
                            placeholder="Chat title"
                            $customStyles={{ marginBottom: 0 }}
                          />
                        </Box>
                        <Box
                          onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleSaveTitle(session.id); }}
                          sx={{ cursor: "pointer", fontSize: "14px" }}
                        >
                          ✓
                        </Box>
                      </Flex>
                    ) : (
                      <Flex sx={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <P fontSize="14px" sx={{ 
                            fontWeight: 500, 
                            marginBottom: "4px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}>
                            {session.title || "New Chat"}
                          </P>
                          <P fontSize="11px" color={themevals.colors.dkgrey}>
                            {new Date(session.updatedAt || session.createdAt).toLocaleDateString()}
                            {session.messageCount ? ` · ${session.messageCount} msgs` : ""}
                          </P>
                        </Box>
                        <Flex sx={{ gap: "4px", marginLeft: "8px" }}>
                          <Box
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setEditingSessionId(session.id);
                              setEditTitle(session.title || "");
                            }}
                            sx={{ 
                              cursor: "pointer", 
                              fontSize: "12px", 
                              opacity: 0.6,
                              "&:hover": { opacity: 1 }
                            }}
                          >
                            ✎
                          </Box>
                          <Box
                            onClick={(e: React.MouseEvent) => handleDeleteSession(session.id, e)}
                            sx={{ 
                              cursor: "pointer", 
                              fontSize: "12px", 
                              opacity: 0.6,
                              "&:hover": { opacity: 1, color: "red" }
                            }}
                          >
                            ✕
                          </Box>
                        </Flex>
                      </Flex>
                    )}
                  </Box>
                ))
              )}
            </Box>

            {/* Tool Access Info */}
            {toolAccess && (
              <Box sx={{ padding: "12px", borderTop: `1px solid ${themevals.colors.ltgrey}`, fontSize: "11px", color: themevals.colors.dkgrey }}>
                {toolAccess.tools.length} tools · {toolAccess.canWrite ? "read/write" : "read-only"}
              </Box>
            )}
          </Box>

          {/* Chat Area */}
          <Box sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${themevals.colors.mdgrey}`,
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: themevals.colors.white,
          }}>
            {/* Provider & Model Selector Header */}
            <Box sx={{
              padding: "12px 20px",
              borderBottom: `1px solid ${themevals.colors.mdgrey}`,
              backgroundColor: "#F9FAFB",
            }}>
              <Flex sx={{ alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                {/* Provider Toggle */}
                <Flex sx={{ alignItems: "center", gap: "8px" }}>
                  <P fontSize="13px" color={themevals.colors.dkgrey} sx={{ margin: 0 }}>Provider:</P>
                  <Flex sx={{ gap: "4px" }}>
                    <Box
                      onClick={() => setProvider('openai')}
                      sx={{
                        padding: "5px 10px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 500,
                        backgroundColor: provider === 'openai' ? themevals.colors.info : "transparent",
                        color: provider === 'openai' ? themevals.colors.white : themevals.colors.dkgrey,
                        border: `1px solid ${provider === 'openai' ? themevals.colors.info : themevals.colors.mdgrey}`,
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: themevals.colors.info,
                        },
                      }}
                    >
                      OpenAI
                    </Box>
                    <Box
                      onClick={() => setProvider('claude')}
                      sx={{
                        padding: "5px 10px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 500,
                        backgroundColor: provider === 'claude' ? "#D97706" : "transparent",
                        color: provider === 'claude' ? themevals.colors.white : themevals.colors.dkgrey,
                        border: `1px solid ${provider === 'claude' ? "#D97706" : themevals.colors.mdgrey}`,
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "#D97706",
                        },
                      }}
                    >
                      Claude
                    </Box>
                  </Flex>
                </Flex>

                {/* Model Dropdown */}
                <Flex sx={{ alignItems: "center", gap: "8px" }}>
                  <P fontSize="13px" color={themevals.colors.dkgrey} sx={{ margin: 0 }}>Model:</P>
                  <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    style={{
                      padding: "5px 10px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 500,
                      border: `1px solid ${themevals.colors.mdgrey}`,
                      backgroundColor: themevals.colors.white,
                      cursor: "pointer",
                      outline: "none",
                      minWidth: "160px",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    {MODEL_OPTIONS[provider].map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </Flex>
              </Flex>
            </Box>

            {/* Messages Area */}
            <Box sx={{
              flex: 1,
              overflowY: "auto",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}>
              {messages.length === 0 && (
                <Box sx={{ textAlign: "center", color: themevals.colors.dkgrey, marginTop: "40px" }}>
                  <H2 fontFamily="Poppins" fontSize="20px" color={themevals.colors.black}>
                    👋 How can I help you today?
                  </H2>
                  <P fontSize="14px" color={themevals.colors.dkgrey} sx={{ marginTop: "8px", marginBottom: "24px" }}>
                    I can help you with financial data, clients, invoices, projects, and more.
                  </P>
                  
                  <Flex sx={{ flexWrap: "wrap", gap: "8px", justifyContent: "center", maxWidth: "600px", margin: "0 auto" }}>
                    {[
                      "What's our current financial position?",
                      "Show me overdue invoices",
                      "Which clients have outstanding balances?",
                      "What's in our approved pipeline?",
                    ].map((prompt) => (
                      <Box
                        key={prompt}
                        onClick={() => setInputValue(prompt)}
                        sx={{
                          backgroundColor: themevals.colors.ltgrey,
                          color: themevals.colors.white,
                          padding: "8px 12px",
                          borderRadius: "16px",
                          fontSize: "13px",
                          fontFamily: "Poppins, sans-serif",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: themevals.colors.mdgrey,
                          },
                        }}
                      >
                        {prompt}
                      </Box>
                    ))}
                  </Flex>
                </Box>
              )}

              {messages.filter(m => m.role !== 'tool').map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}

              {isLoading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", color: themevals.colors.dkgrey }}>
                  <Spinner size={16} />
                  <span>Thinking...</span>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>

            {/* Error Display */}
            {error && (
              <Box sx={{
                padding: "12px 20px",
                backgroundColor: "#FEE2E2",
                color: "#DC2626",
                fontSize: "14px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
                <span>{error}</span>
                <Box onClick={() => setError(null)} sx={{ cursor: "pointer", padding: "4px 8px" }}>✕</Box>
              </Box>
            )}

            {/* Input Area */}
            <Box sx={{
              borderTop: `1px solid ${themevals.colors.mdgrey}`,
              padding: "16px 20px",
              backgroundColor: "#F9FAFB",
            }}>
              <Flex sx={{ gap: "12px", alignItems: "center" }}>
                <Box sx={{ flex: 1 }}>
                  <TextInput
                    value={inputValue}
                    onChange={(val: string) => setInputValue(val)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about finances, clients, invoices, projects..."
                    disabled={isLoading || !toolAccess?.canRead}
                    $customStyles={{ marginBottom: 0 }}
                  />
                </Box>
                <BasicButton
                  scheme="primary"
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading || !toolAccess?.canRead}
                  $submitting={isLoading}
                >
                  Send
                </BasicButton>
              </Flex>
            </Box>
          </Box>
        </Flex>
      </Page>
    </>
  );
};

// Message Bubble Component
const MessageBubble = ({ message }: { message: ChatMessage }) => {
  const isUser = message.role === "user";

  return (
    <Flex sx={{ justifyContent: isUser ? "flex-end" : "flex-start" }}>
      <Box sx={{
        maxWidth: "80%",
        padding: "14px 18px",
        borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
        backgroundColor: isUser ? "#DDEDFF" : "#F3F4F6",
        color: isUser ? themevals.colors.info : themevals.colors.black,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}>
        <Box sx={{
          fontSize: "14px",
          lineHeight: 1.6,
          fontFamily: "Poppins, sans-serif",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}>
          {message.content}
        </Box>

        <Box sx={{
          fontSize: "11px",
          opacity: 0.6,
          marginTop: "8px",
          textAlign: isUser ? "right" : "left",
        }}>
          {new Date(message.timestamp || message.createdAt || Date.now()).toLocaleTimeString([], { 
            hour: "2-digit", 
            minute: "2-digit" 
          })}
        </Box>
      </Box>
    </Flex>
  );
};

export default AIChat;
